import { Cart, useCart } from "app/hooks/checkout/useCart";
import { useAnalytics } from "app/hooks/shared/analytics/useAnalytics";
import { useMontageService } from "app/hooks/shared/standard-product/useMontageService";
import { useStandardProductsDeliveryMethod } from "app/hooks/shared/standard-product/useStandardProductsDeliveryMethod";
import {
  zakelijkDoorCodes,
  zakelijkProfielCodes,
} from "features/option-controls/door-width-controls/ZakelijkHelper";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useQueryGates } from "app/hooks/shared/useQueryGates";
import {
  CategoryStandardProduct,
  CreateCartPayload,
  Product,
  ValidateDiscountCodeResponse,
  WarrantyPackage,
  WarrantyPackageVoucher,
} from "shared/api/middleware-api-client";
import {
  STORAGE_IS_SAME_ADDRESS,
  STORAGE_ORDER_KEY,
  STORAGE_PERSONAL_INFO_TYPE,
} from "shared/config";
import { getFormattedPrice } from "shared/utils/getFormattedPrice";
import {
  MONTAGE_SERVICE_TYPE_CODE,
  PANEL_CATEGORY_CODE,
  PANEL_SET_CODES,
  STANDARD_PRODUCT_PAYMENT_METHOD,
  STANDARD_PRODUCT_PAYMENT_TYPE,
} from "shared/utils/standard-product";
import { encryptVoucher } from "shared/utils/voucher";
import { useStandardProductDeliveryCostsCalculator } from "../hooks/shared/standard-product/useStandardProductDeliveryCostsCalculator";
import { ConfiguredProduct } from "./DoorOptionsProvider";
import { useDealerDiscounts } from "app/hooks/checkout/useDealerDiscounts";
import { useWarrantyVoucherData } from "app/hooks/checkout/useWarrantyVoucherData";
import { DELIVERY_PRICES } from "shared/config/pricing";
import {
  hasEqualWidths,
  requiresEqualWidths,
} from "entities/configurator/model/widthSymmetry";
import { getStandingLayoutMinWidth } from "entities/configurator/model/widthLimits";

const clamp = require("lodash.clamp");
export interface CartProviderProps extends Cart {
  cart: CreateCartPayload;
  personalInfoType: string;
  isSameAddress: boolean;
  isSampleSale: boolean;
  monthlyPayment: number;
  remaining: string;
  method: string;

  addWarrantyPackage: (warrantyPackage: WarrantyPackage) => void;
  getFormattedWarrantyPackagePrice: () => string;
  getCartProduct: (index: number) => ConfiguredProduct | undefined;
  removeCartProduct: (index: number) => void;
  removeWarrantyPackage: () => void;
  resetCart: () => void;
  getCart: () => CreateCartPayload;
  setCartToDefault: () => void;
  duplicateCartProduct: (index: number) => void;
  addCartProduct: (product: Product, index?: number) => boolean;
  getCartLinePrice: (product: Product) => number;
  getTotalPrice: (paymentMethod?: string) => string;
  getTotalPriceUnformatted: (paymentMethod?: string) => number;
  getFormattedSubTotalPrice: () => string;
  getFormattedVoucherDiscount: () => string;
  getRawSelfAssemblyDiscount: () => number;
  setMonthlyPayment: React.Dispatch<React.SetStateAction<number>>;
  setMethod: React.Dispatch<React.SetStateAction<string>>;
  getRawTotalPrice: (paymentMethod?: string) => number;
  getHumanReadableTotalRemaining: () => string;

  newVoucher: ValidateDiscountCodeResponse | null;
  getVoucherDisplay: () => string;
  setVoucher: (
    voucher: ValidateDiscountCodeResponse,
    discountCodeInitialised: boolean,
  ) => void;
  getAbsoluteVoucherDiscount: () => string;
  removeVoucher: () => void;

  addCartStandardProduct: (panel: CategoryStandardProduct) => void;
  addCartAssemblyProduct: (assemblyProducts: CategoryStandardProduct[]) => void;
  removeCartAssemblyProduct: (
    assemblyProducts: CategoryStandardProduct[],
  ) => void;
  updateCartStandardProduct: (
    id: number,
    panel: CategoryStandardProduct,
  ) => void;
  removeCartStandardProduct: (index: number) => void;
  changeCartStandardProductAmount: (index: number, amount: number) => void;
  changePickupWarehouse: (warehouse: string) => void;
  updateGeneralCartValue: (object: object) => void;
  changeWithAssembly: (toggle: boolean) => void;
  changeCartStandardProductPickupDate: (date: string) => void;
  changeCartStandardProductDeliveryDate: (date: string) => void;
  changeCartStandardProductPaymentBank: (bank: string) => void;
  changeCartStandardProductPaymentMethod: (method: string) => void;

  getAllExtraDiscounts: () => number;
  containsZakelijkProducts: boolean;
  containsOnlyZakelijkProfiles: boolean;
  getAllZakelijkDiscounts: () => number;
  cartType: string;
  cartHasDiscountCode: boolean;
  voucherWarranties: WarrantyPackageVoucher[] | undefined;
}

// TODO: Extract hooks: useCartStandardProducts, useCartProducts, useCartWarranty, useCartVoucher, useCart
const CartContext = createContext<CartProviderProps>({} as CartProviderProps);
export const useCartContext = () => useContext(CartContext);
const CartProvider = ({ children }: any) => {
  const [searchParams] = useSearchParams();

  const {
    getCart: getCartValue,
    updateCart,
    getDefaultCart,
    setCartToDefault,
  } = useCart();
  const cartValue = getCartValue();

  const {
    onAddToCart,
    onRemoveFromCart,
    onAddStandardProductToCart,
    onRemoveStandardProductFromCart,
  } = useAnalytics();
  const { calculateDeliveryMethod } = useStandardProductsDeliveryMethod();

  // Zakelijk discount
  const { businessDoorDiscount } = useDealerDiscounts();

  // Query Gates
  const { enableDeliveryCostsQuery, enableWarrantyOrPayment } = useQueryGates();

  const { getAssemblyProductsForCart } =
    useStandardProductDeliveryCostsCalculator(
      cartValue,
      true,
      enableDeliveryCostsQuery,
    );

  const personalInfoType =
    localStorage.getItem(STORAGE_PERSONAL_INFO_TYPE) || "personal";

  const containsZakelijkProducts = useMemo(
    () =>
      cartValue.products.some((product) =>
        zakelijkDoorCodes.includes(product.doorType.code),
      ),
    [cartValue],
  );

  const containsOnlyZakelijkProfiles = useMemo(
    () =>
      cartValue.products.every((product) =>
        zakelijkProfielCodes.includes(product.doorType.code),
      ),
    [cartValue],
  );

  const isSameAddress = useMemo(() => {
    if (containsZakelijkProducts) {
      return false;
    }
    return JSON.parse(localStorage.getItem(STORAGE_IS_SAME_ADDRESS) || "true");
  }, [containsZakelijkProducts]);

  const [monthlyPayment, setMonthlyPayment] = useState<number>(0);
  const [newVoucher, setNewVoucher] =
    useState<ValidateDiscountCodeResponse | null>(null);
  const [method, setMethod] = useState<string>("");
  const [remaining, setRemaining] = useState<string>("");

  const { freeGlueProduct } = useMontageService(enableDeliveryCostsQuery);

  const voucherWarranties = useMemo(
    () => newVoucher?.warrantyPackages,
    [newVoucher],
  );

  // Warranty voucher data
  const { voucherWarrantyPackage } = useWarrantyVoucherData({
    cartValue,
    voucherWarranties,
    enabled: enableWarrantyOrPayment,
  });

  useEffect(() => {
    let method = searchParams.get("method") || "PAY_LATER";
    setMethod(method);

    if (method === "PAY_NOW") {
      let price = getRawTotalPrice("PAY_NOW");
      price = price * 0.1;
      setRemaining(getFormattedPrice(price));
    }

    if (method === "PAY_LATER") {
      setRemaining("€" + " 0,-");
    }
    if (method === "PAY_FULL") {
      setRemaining(getTotalPrice("PAY_FULL"));
    }
  }, [searchParams]);

  useEffect(() => {
    if (cartValue.isSelfAssembly) {
      let price = getRawTotalPrice();
      setRemaining(getFormattedPrice(price));
    }
  }, [cartValue.totalPrice]);

  const addCartProduct = (product: Product, index?: number) => {
    const hasVerticalBeams = product.measurements.verticalBeams?.some(
      (positions) => positions.length > 0,
    );
    const standingMinimumWidth = getStandingLayoutMinWidth(
      product.doorType.code,
      !!hasVerticalBeams,
    );
    if (
      standingMinimumWidth !== undefined &&
      (!product.measurements.widths?.length ||
        product.measurements.widths.some(
          (width) => width < standingMinimumWidth,
        ))
    ) {
      toast.error(
        `Deze indeling vereist een minimale breedte van ${standingMinimumWidth} mm per element. Pas de breedte aan voordat je de deur toevoegt.`,
      );
      return false;
    }

    if (
      requiresEqualWidths(
        product.doorType.code,
        product.doorLayout.code,
        hasVerticalBeams,
      ) &&
      !hasEqualWidths(product.measurements.widths)
    ) {
      toast.error(
        "Deze indeling vereist gelijke deurbreedtes. Pas de breedte aan voordat je de deur toevoegt.",
      );
      return false;
    }

    const cartValue = getCartValue();

    //TEMPORARY FIX
    if (!product.measurements.rotation) {
      product.measurements.rotation = "left";
    }
    onAddToCart(product);

    if (index !== undefined) {
      const updatedCart = { ...cartValue };
      updatedCart.products[index] = product;
      updateCart(updatedCart);
      return true;
    }

    const updatedCart: CreateCartPayload = {
      ...cartValue,
      products: [...cartValue.products, product],
    };

    updatedCart.isSelfAssembly = false;
    if (product.isSelfAssembly) {
      updatedCart.isSelfAssembly = true;
      updatedCart.deliveryCosts = DELIVERY_PRICES.SELF_ASSEMBLY; // Default delivery costs for self-assembly product
    }
    // Update delivery price for zakelijk
    if (zakelijkDoorCodes.includes(product.doorType.code)) {
      updatedCart.deliveryCosts = DELIVERY_PRICES.ZAKELIJK; // Default delivery costs for self-assembly product
    }

    updateCart(updatedCart);
    return true;
  };

  const getCartProduct = (index: number) => {
    const product = cartValue.products.at(index);
    if (!product) return;
    return product;
  };

  const isSampleSale = !!(cartValue.sampleSale && cartValue.shoppingCartId);

  const duplicateCartProduct = (index: number) => {
    const updatedCart = { ...cartValue };
    updatedCart.products = [
      ...updatedCart.products.slice(0, index + 1),
      ...updatedCart.products.slice(index, updatedCart.products.length),
    ];

    onAddToCart(cartValue.products[index]);
    updateCart(updatedCart);
  };

  const removeCartProduct = (index: number) => {
    if (!cartValue) return;

    const product = cartValue.products.at(index)!;
    onRemoveFromCart(product);

    const updatedCart = { ...cartValue };
    updatedCart.products.splice(index, 1);

    if (product?.cornerWallIndex) {
      let cornerIndex = cartValue.products.findIndex(
        (value) => value.cornerWallIndex === product.cornerWallIndex,
      );
      updatedCart.products.splice(cornerIndex, 1);
    }

    if (cartValue.products.length === 1) {
      setMonthlyPayment(0);
    }
    if (updatedCart.products.length === 0) {
      updatedCart.isSelfAssembly = false;
    }

    updateCart(updatedCart);

    if (updatedCart.products.length === 0) {
      resetCart();
      localStorage.removeItem(STORAGE_ORDER_KEY);
    }
  };

  const addCartStandardProduct = (standardProduct: CategoryStandardProduct) => {
    onAddStandardProductToCart(standardProduct);

    const isAkoesticPanel =
      standardProduct.productType.code === "akoestischepanelen";
    // Whether a product already exists in the cart (index if yes, --1 if doesnt exist)
    const existingProductIndex = cartValue.standardProducts.findIndex(
      (product) =>
        product.name === standardProduct.name &&
        product.productTypeId === standardProduct.productTypeId,
    );

    // If product already exists in the cart, just update the amount
    if (existingProductIndex !== -1) {
      const updatedCartValue = {
        ...cartValue,
        standardProducts: cartValue.standardProducts.map((product, index) => {
          if (index === existingProductIndex) {
            return {
              ...product,
              amount: product.amount + standardProduct.amount,
            };
          }
          // If the product that is being changed is an acoustic panel ->
          // change the amount of the free glue product as well
          if (
            isAkoesticPanel &&
            freeGlueProduct &&
            product.id === freeGlueProduct.id
          ) {
            const productAmount = PANEL_SET_CODES.includes(standardProduct.code)
              ? standardProduct.amount * 2
              : standardProduct.amount;

            return { ...product, amount: product.amount + productAmount };
          }
          return product;
        }),
      };

      updateCart(updatedCartValue);
    } else {
      const newStandardProduct = {
        ...standardProduct,
        amount: standardProduct.amount || 1,
      };

      const updatedStandardProducts = [
        ...cartValue.standardProducts,
        newStandardProduct,
      ];

      // If its a new product, check if the free glue product is already in the cart
      if (isAkoesticPanel && freeGlueProduct) {
        const freeGlueProductIndex = cartValue.standardProducts.findIndex(
          (product) => product.id === freeGlueProduct.id,
        );

        //If the glue product is there, its amount needs to be updated
        if (freeGlueProductIndex !== -1) {
          const productAmount = PANEL_SET_CODES.includes(standardProduct.code)
            ? standardProduct.amount * 2
            : standardProduct.amount;

          updatedStandardProducts[freeGlueProductIndex] = {
            ...updatedStandardProducts[freeGlueProductIndex],
            amount:
              updatedStandardProducts[freeGlueProductIndex].amount +
              productAmount,
          };
        }
      }

      const containsAcousticPanels = updatedStandardProducts.some(
        (product) => product.productType.code === PANEL_CATEGORY_CODE,
      );

      const someAccessoireAkoestischPaneel = updatedStandardProducts.some(
        (product) => product.productType.code === "accessoireakoestischpaneel",
      );

      const containsMontageService = updatedStandardProducts.some(
        (product) => product.productType.code === MONTAGE_SERVICE_TYPE_CODE,
      );

      // Otherwise, add the new product
      const updatedCartValue = {
        ...cartValue,
        standardProducts: updatedStandardProducts,
        pickupWarehouse:
          (containsAcousticPanels || someAccessoireAkoestischPaneel) &&
          !cartValue.withAssembly
            ? "ESD"
            : "",
      };

      if (containsMontageService) {
        addCartAssemblyProduct(
          getAssemblyProductsForCart(updatedCartValue),
          updatedCartValue,
        );
      } else {
        updateCart(updatedCartValue);
      }
    }
  };

  const addCartAssemblyProduct = (
    assemblyProducts: CategoryStandardProduct[],
    cart?: CreateCartPayload,
  ) => {
    const updatedAssemblyProducts: CategoryStandardProduct[] = [];

    const cartToUse = cart !== undefined ? cart : cartValue;

    assemblyProducts.forEach((assemblyProduct) => {
      const existingProductIndex = cartToUse.standardProducts.findIndex(
        (product) =>
          product.name === assemblyProduct.name &&
          product.id === assemblyProduct.id,
      );

      // Remove existing assembly product, to prevent double items
      if (existingProductIndex !== -1) {
        cartToUse.standardProducts.splice(existingProductIndex, 1);
      }

      const newStandardProduct = {
        ...assemblyProduct,
        amount: assemblyProduct.amount || 1,
      };

      updatedAssemblyProducts.push(newStandardProduct);
    });

    const updatedStandardProducts = [
      ...cartToUse.standardProducts,
      ...updatedAssemblyProducts,
    ];

    const updatedCartValue = {
      ...cartToUse,
      standardProducts: updatedStandardProducts,
    };

    updateCart(updatedCartValue);
  };

  const removeCartAssemblyProduct = (
    assemblyProducts: CategoryStandardProduct[],
  ) => {
    assemblyProducts.forEach((assemblyProduct) => {
      const existingProductIndex = cartValue.standardProducts.findIndex(
        (product) =>
          product.name === assemblyProduct.name &&
          product.id === assemblyProduct.id,
      );
      // Remove existing assembly product, to prevent double items
      if (existingProductIndex !== -1) {
        cartValue.standardProducts.splice(existingProductIndex, 1);
      }
    });

    const updatedStandardProducts = [...cartValue.standardProducts];

    const updatedCartValue = {
      ...cartValue,
      standardProducts: updatedStandardProducts,
    };

    updateCart(updatedCartValue);
  };

  const updateCartStandardProduct = (
    id: number,
    standardProduct: CategoryStandardProduct,
  ) => {
    updateCart({
      ...cartValue,
      standardProducts: cartValue.standardProducts.map((panel, index) => {
        if (index === id) return standardProduct;
        return panel;
      }),
    });
  };

  const changeCartStandardProductAmount = (index: number, amount: number) => {
    const standardProduct = cartValue.standardProducts[index];
    const isAkoesticPanel =
      standardProduct.productType.code === "akoestischepanelen";
    const amountChange = amount - standardProduct.amount;

    updateCart({
      ...cartValue,
      standardProducts: cartValue.standardProducts.map((panel, id) => {
        if (id === index) return { ...panel, amount };

        // If the product that is being changed is an acoustic panel ->
        // change the amount of the free glue product as well
        if (
          isAkoesticPanel &&
          freeGlueProduct &&
          panel.id === freeGlueProduct.id
        ) {
          const productAmount = PANEL_SET_CODES.includes(standardProduct.code)
            ? amountChange * 2
            : amountChange;

          return { ...panel, amount: panel.amount + productAmount };
        }
        return panel;
      }),
    });
  };

  const changeCartStandardProductPickupDate = (date: string) => {
    updateCart({
      ...cartValue,
      delivery: {
        type: cartValue.delivery.type,
        date,
      },
    });
  };

  const changeCartStandardProductDeliveryDate = (date: string) => {
    updateCart({
      ...cartValue,
      delivery: {
        type: cartValue.delivery.type,
        date,
      },
    });
  };

  const changePickupWarehouse = (warehouse: string) => {
    updateCart({
      ...cartValue,
      pickupWarehouse: warehouse,
    });
  };

  const updateGeneralCartValue = (update: object) => {
    updateCart({
      ...cartValue,
      ...update,
    });
  };

  const changeWithAssembly = (toggle: boolean) => {
    updateCart({
      ...cartValue,
      withAssembly: true,
    });
  };

  const removeCartStandardProduct = (index: number) => {
    // Send analytics first
    const removableProduct = cartValue.standardProducts[index];

    // Send analytics with the removable product
    onRemoveStandardProductFromCart(removableProduct);

    let newStandardProducts: CategoryStandardProduct[] = [];

    // Remove product
    newStandardProducts = cartValue.standardProducts.filter(
      (_, id) => id !== index,
    );

    // Check if there are any acoustic panels left in the cart
    const containsAcousticPanels = newStandardProducts.some(
      (product) => product.productType.code === PANEL_CATEGORY_CODE,
    );

    // Check if there are any montage services left in the cart
    const containsMontageService = newStandardProducts.some(
      (product) => product.productType.code === MONTAGE_SERVICE_TYPE_CODE,
    );

    //Check if there are any accessoire akoestisch paneel in the cart
    const someAccessoireAkoestischPaneel = newStandardProducts.some(
      (product) => product.productType.code === "accessoireakoestischpaneel",
    );

    if (!containsAcousticPanels) {
      newStandardProducts = newStandardProducts.filter(
        (product) =>
          product.productType.code !== MONTAGE_SERVICE_TYPE_CODE &&
          product.productType.code !== "montageachtergrond",
      );

      /**
       * Cart: only has accesoire products (glass, verfset) => everything to default
       * Cart: has accessoireakoestischpaneel => pickupWarehouse = ESD
       */
      updateCart({
        ...cartValue,
        standardProducts: newStandardProducts,
        withAssembly: false,
        bumbalFlow: "",
        pickupWarehouse: someAccessoireAkoestischPaneel ? "ESD" : "",
      });
    } else {
      //If for e.g. there were 2 acoustic panels
      //-> we remove only 1 ->
      // we need to update the amount of the glue product as well
      if (freeGlueProduct) {
        const freeGlueProductIndex = newStandardProducts.findIndex(
          (product) => product.id === freeGlueProduct.id,
        );

        if (freeGlueProductIndex !== -1) {
          newStandardProducts[freeGlueProductIndex] = {
            ...newStandardProducts[freeGlueProductIndex],
            amount:
              newStandardProducts[freeGlueProductIndex].amount -
              removableProduct.amount,
          };
        }
      }

      //If there are acoustic panels and no montage service, just remove them
      updateCart({
        ...cartValue,
        standardProducts: newStandardProducts,
        withAssembly: containsMontageService,
      });
    }

    if (newStandardProducts.length === 0) {
      resetCart();
      localStorage.removeItem(STORAGE_ORDER_KEY);
    }
  };

  const changeCartStandardProductPaymentBank = (bank: string) => {
    updateCart({
      ...cartValue,
      standardProductPayment: {
        paymentMethod: STANDARD_PRODUCT_PAYMENT_METHOD,
        paymentType: STANDARD_PRODUCT_PAYMENT_TYPE,
        paymentBank: parseInt(bank) || 0,
      },
    });
  };

  const changeCartStandardProductPaymentMethod = (method: string) => {
    updateCart({
      ...cartValue,
      standardProductPayment: {
        paymentBank: 0,
        paymentMethod: parseInt(method),
        paymentType: STANDARD_PRODUCT_PAYMENT_TYPE,
      },
    });
  };

  const removeWarrantyPackage = () => {
    updateCart({ ...cartValue, warrantyPackage: undefined });
  };

  const getCart = () => cartValue;

  const resetCart = (): void => {
    updateCart(getDefaultCart());
  };

  const getSubTotalPrice = (): number => {
    let totalPrice =
      cartValue.totalPrice -
      (cartValue.warrantyPackage ? cartValue.warrantyPackage.price : 0);

    if (cartValue.withAssembly) {
      cartValue.standardProducts.forEach((product) => {
        if (product.productType.code === MONTAGE_SERVICE_TYPE_CODE) {
          totalPrice -= product.price * product.amount;
        }
      });
    }

    return totalPrice;
  };

  const getFormattedSubTotalPrice = (): string => {
    return getFormattedPrice(getSubTotalPrice());
  };

  const getRawSelfAssemblyDiscount = (): number => {
    return cartValue.products.reduce(
      (discount, product) => discount + (product?.selfAssemblyDiscount || 0),
      0,
    );
  };

  const getSampleSaleDiscount = (): number => {
    const sampleSaleDiscount = cartValue.discountRecords?.[0];
    if (sampleSaleDiscount) {
      let discount = sampleSaleDiscount.value;
      let type = sampleSaleDiscount.type;
      if (type === "abs") {
        return sampleSaleDiscount.value;
      } else if (type === "rel") {
        return (
          (cartValue.totalPrice - getAllExtraDiscounts()) * (discount / 100)
        );
      }
    }
    return 0;
  };

  const getRawVoucherDiscount = (): number | null => {
    if (!newVoucher) return null;

    // Add delivery costs when choosing delivery
    let standardDeliveryPrice = 0;
    if (cartValue.pickupWarehouse === "" && !cartValue.withAssembly) {
      standardDeliveryPrice =
        calculateDeliveryMethod(cartValue.standardProducts)?.price || 0;
    }

    const extraDiscounts = getAllExtraDiscounts();
    const basePrice = voucherWarrantyPackage
      ? voucherWarrantyPackage.price
      : Math.max(
          0,
          cartValue.totalPrice + standardDeliveryPrice - extraDiscounts,
        );

    const { type, value } = newVoucher;

    if (type === "abs") {
      // absolute amount; never more than base or below 0
      return value;
    }

    if (type === "rel") {
      // relative percent; value is percentage (e.g., 15 → 15%)
      const pct = value / 100;
      return clamp(basePrice * pct, 0, basePrice);
    }

    // Unknown voucher type
    return null;
  };

  const getFormattedVoucherDiscount = (): string => {
    if (getRawVoucherDiscount()) {
      return getFormattedPrice(getRawVoucherDiscount(), true);
    }
    return `€0.-`;
  };

  const getCartLinePrice = (product: Product): number => {
    let price = 0;

    // Build up price from API
    Object.values(product).forEach((line: any) => {
      if (line?.price) {
        price += line.price;
      }
    });

    return price;
  };

  // Eigenwijs discounts + color discounts
  const getAllExtraDiscounts = (): number => {
    if (containsZakelijkProducts) {
      return 0;
    }
    //Total nr of products
    const totalProductsCount = cartValue.products.length;

    // Count eigenwijs doors and doors with color that have
    const { eigenwijsCount, priceyDoorsCount } = cartValue.products.reduce(
      (acc, product) => {
        // Eigenwijs doors
        if (product.doorLayout?.code === "eigenwijs") {
          acc.eigenwijsCount++;
        }

        // Doors with color that have price > 0
        if (product.doorColor?.price && product.doorColor.price > 0) {
          acc.priceyDoorsCount++;
        }
        return acc;
      },
      { eigenwijsCount: 0, priceyDoorsCount: 0 },
    );

    // Discount 150 per eigenwijs door after the first one
    const eigenwijsDiscount =
      eigenwijsCount > 1 ? (eigenwijsCount - 1) * 150 : 0;

    // Discount 100 per door with color after the first one
    const colorDiscount =
      priceyDoorsCount > 1 ? (priceyDoorsCount - 1) * 100 : 0;

    // Additional doors discount
    const additionalDiscount =
      totalProductsCount > 1 ? (totalProductsCount - 1) * 50 : 0;

    return eigenwijsDiscount + colorDiscount + additionalDiscount;
  };

  const getZakelijkTotalPrice = (): number => {
    let price = cartValue.totalPrice;

    if (containsZakelijkProducts) {
      price -= getAllZakelijkDiscounts();
    }

    // Subtract voucher discount
    if (getRawVoucherDiscount()) {
      price = price - getRawVoucherDiscount()!;
    }

    if (cartValue.deliveryCosts) {
      price += DELIVERY_PRICES.ZAKELIJK;
    }

    return price;
  };

  const getRawTotalPrice = (paymentMethod?: string): number => {
    let price = cartValue.totalPrice;

    // Eigenwijs discounts + color discounts
    const allExtraDiscount = getAllExtraDiscounts();
    price = price - allExtraDiscount;

    // Add delivery costs when choosing delivery
    if (cartValue.pickupWarehouse === "" && !cartValue.withAssembly) {
      price += calculateDeliveryMethod(cartValue.standardProducts)?.price || 0;
    }

    // Subtract voucher discount
    if (getRawVoucherDiscount()) {
      price = price - getRawVoucherDiscount()!;
    }

    if (isSampleSale) {
      price = price - getSampleSaleDiscount();
    }

    if (containsZakelijkProducts) {
      price -= getAllZakelijkDiscounts();
    }

    // Add pay_full discount
    if (paymentMethod && paymentMethod === "PAY_FULL") {
      price = price * 0.98;
    }

    // Add delivery costs when self assembly door
    if (cartValue.isSelfAssembly && cartValue.deliveryCosts) {
      price += cartValue.deliveryCosts;
    }

    return price;
  };

  const getTotalPriceUnformatted = (paymentMethod?: string): number => {
    if (containsZakelijkProducts) {
      return getZakelijkTotalPrice();
    }
    return getRawTotalPrice(paymentMethod);
  };

  const getTotalPrice = (paymentMethod?: string): string => {
    if (containsZakelijkProducts) {
      return getFormattedPrice(getZakelijkTotalPrice());
    }
    return getFormattedPrice(getRawTotalPrice(paymentMethod));
  };

  const addWarrantyPackage = (warrantyPackage: WarrantyPackage) => {
    updateCart({
      ...cartValue,
      warrantyPackage: warrantyPackage,
    });
  };

  const getFormattedWarrantyPackagePrice = () => {
    if (voucherWarrantyPackage) {
      const voucherDiscount = getRawVoucherDiscount() ?? 0;
      return getFormattedPrice(voucherWarrantyPackage.price - voucherDiscount);
    }
    return getFormattedPrice(cartValue.warrantyPackage?.price);
  };

  const getHumanReadableTotalRemaining = () => {
    if (containsZakelijkProducts) {
      const vatIncludedTotalPrice = getZakelijkTotalPrice() * 1.21;
      return getFormattedPrice(vatIncludedTotalPrice);
    }
    return remaining;
  };

  // TODO: Extract to voucher hook
  const setVoucher = (
    voucher: ValidateDiscountCodeResponse,
    discountCodeInitialised = false,
  ): void => {
    setNewVoucher(voucher);
    if (!discountCodeInitialised) {
      applyDiscountCode(encryptVoucher(voucher.code));
    }
  };

  const removeVoucher = (): void => {
    setNewVoucher(null);
    applyDiscountCode("");
  };

  const getVoucherDisplay = (): string => {
    if (newVoucher) {
      if (newVoucher.type === "abs") {
        return `${newVoucher.value} euro`;
      } else if (newVoucher.type === "rel") {
        return `${newVoucher.value}%`;
      }
    }
    return "";
  };

  const getAbsoluteVoucherDiscount = (): string => {
    if (getRawVoucherDiscount()) {
      return getFormattedPrice(getRawVoucherDiscount(), true);
    }
    return getFormattedPrice(0);
  };

  const applyDiscountCode = (discountCode: string) => {
    updateCart({ ...cartValue, discountCode });
  };

  const getAllZakelijkDiscounts = () => {
    const zakelijkDiscount =
      (cartValue.products.reduce((sum, product) => {
        if (zakelijkDoorCodes.includes(product.doorType.code)) {
          return sum + (product.totalPrice || 0);
        }
        return sum;
      }, 0) *
        businessDoorDiscount) /
      100;

    return zakelijkDiscount;
  };

  // Determine cart type for voucher application
  const cartType = cartValue.products.length > 0 ? "order" : "standardOrder";

  // Whether the cart has a discount code applied
  const cartHasDiscountCode = !!cartValue.discountCode;

  return (
    <CartContext.Provider
      value={{
        cart: cartValue,
        addWarrantyPackage,
        getFormattedWarrantyPackagePrice,
        personalInfoType,
        isSameAddress,
        isSampleSale,
        getCart,
        resetCart,
        updateCart,
        getDefaultCart,
        addCartProduct,
        getCartProduct,
        setCartToDefault,
        duplicateCartProduct,
        removeCartProduct,
        removeWarrantyPackage,
        getCartLinePrice,
        getTotalPrice,
        getTotalPriceUnformatted,
        getFormattedSubTotalPrice,
        getFormattedVoucherDiscount,
        monthlyPayment,
        setMonthlyPayment,
        remaining,
        method,
        setMethod,
        getRawTotalPrice,
        getHumanReadableTotalRemaining,
        setVoucher,
        removeVoucher,
        newVoucher,
        getVoucherDisplay,
        getAbsoluteVoucherDiscount,
        getRawSelfAssemblyDiscount,
        addCartStandardProduct,
        addCartAssemblyProduct,
        removeCartAssemblyProduct,
        updateCartStandardProduct,
        removeCartStandardProduct,
        changeCartStandardProductAmount,
        changePickupWarehouse,
        updateGeneralCartValue,
        changeWithAssembly,
        changeCartStandardProductDeliveryDate,
        changeCartStandardProductPickupDate,
        changeCartStandardProductPaymentBank,
        changeCartStandardProductPaymentMethod,

        getAllExtraDiscounts,
        containsZakelijkProducts,
        containsOnlyZakelijkProfiles,
        getAllZakelijkDiscounts,
        cartType,
        cartHasDiscountCode,
        voucherWarranties,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
export default CartProvider;
